import { useQuery } from '@tanstack/react-query';
import { createContext, useContext, useMemo, type CSSProperties, type ReactNode } from 'react';
import { api } from '../api';

/**
 * Owner 2026-10-23 20:49: a contractor's colour marks its events in the calendar and its lines in the quote, the
 * packlist and the quick picker — on screen only (never in a PDF or a printout). The same keys, in the same order, as
 * the server's palette (modules/contractors/src/colors.ts; colors.test.ts checks). The shades are tokens in
 * styles.css: `--kc-<key>` for a stripe, `--kc-<key>-ink` for text (readable on white), `--kc-<key>-tint` for a light
 * background (the calendar tile, owner 2026-10-24 10:35).
 */
export const CONTRACTOR_COLORS = [
  { key: 'blue', label: 'Niebieski' },
  { key: 'sky', label: 'Błękitny' },
  { key: 'teal', label: 'Morski' },
  { key: 'green', label: 'Zielony' },
  { key: 'lime', label: 'Limonkowy' },
  { key: 'yellow', label: 'Żółty' },
  { key: 'orange', label: 'Pomarańczowy' },
  { key: 'red', label: 'Czerwony' },
  { key: 'pink', label: 'Różowy' },
  { key: 'purple', label: 'Fioletowy' },
  { key: 'navy', label: 'Granatowy' },
  { key: 'brown', label: 'Brązowy' },
] as const;

export type ContractorColor = (typeof CONTRACTOR_COLORS)[number]['key'];

const KEYS = new Set<string>(CONTRACTOR_COLORS.map((c) => c.key));
export const isContractorColor = (value: unknown): value is ContractorColor => typeof value === 'string' && KEYS.has(value);

/**
 * The inline custom properties a mark reads (`--mark` for a stripe, `--mark-ink` for text, `--mark-tint` for a light
 * background). No colour, or a key this
 * screen does not know: the neutral default (`--kc-none`, grey — never red, red means an error here).
 */
export function markStyle(key: string | null | undefined): CSSProperties {
  const k = isContractorColor(key) ? key : 'none';
  return { '--mark': `var(--kc-${k})`, '--mark-ink': `var(--kc-${k}-ink)`, '--mark-tint': `var(--kc-${k}-tint)` } as CSSProperties;
}

/** The colour of a calendar dot (owner 2026-10-29 16:02: „kropka odpowiada kolorowi klienta, jeśli nie ma — szara”). */
export function dotColor(key: string | null | undefined): string {
  return `var(--kc-${isContractorColor(key) ? key : 'none'})`;
}

export const CONTRACTOR_COLORS_KEY = ['contractors', 'colors'] as const;

const ColorsContext = createContext<ReadonlyMap<string, ContractorColor>>(new Map());

/**
 * The colours of the contractors by id (`GET /api/contractors/colors`), for everything under it. `enabled`: the
 * person may see contractors; otherwise (and for „Kolor” hidden, rule 7a — the server sends none) there are no colours.
 */
export function ContractorColorsProvider({ enabled = true, children }: { enabled?: boolean; children: ReactNode }) {
  const query = useQuery({
    queryKey: CONTRACTOR_COLORS_KEY,
    enabled,
    retry: false,
    staleTime: 60_000,
    queryFn: () => api<{ colors: Record<string, string> }>('/api/contractors/colors'),
  });
  const colors = useMemo(() => new Map(Object.entries(query.data?.colors ?? {}).filter((e): e is [string, ContractorColor] => isContractorColor(e[1]))), [query.data]);
  return <ColorsContext.Provider value={colors}>{children}</ColorsContext.Provider>;
}

/** Contractor id → its colour key (only those with a colour); empty outside a provider. */
export function useContractorColors(): ReadonlyMap<string, ContractorColor> {
  return useContext(ColorsContext);
}
